import React, { useMemo } from "react";
import {
  DEFAULT_CRITERIA_TREE_BUILDER_LABELS,
  CriteriaTreeBuilderCtx,
} from "./criteria-tree-builder-context";
import { CriteriaTreeBuilderValue } from "./criteria-tree-builder-types";
import { CriteriaTreeBuilderRenderStatementInputArgs } from "./criteria-tree-builder-context";
import { useCriteriaTreeBuilder } from "./useCriteriaTreeBuilder";
import CriteriaGroup from "./CriteriaGroup";
import "./criteria-tree-builder.scss";

export type {
  CriteriaTreeBuilderValue,
  CriteriaStatement,
  CriteriaGroup,
  CriteriaNode,
  CriteriaChecklistNode,
} from "./criteria-tree-builder-types";
export type {
  CriteriaTreeBuilderLabels,
  CriteriaTreeBuilderRenderStatementInputArgs,
} from "./criteria-tree-builder-context";
export {
  createEmptyStatement,
  createEmptyGroup,
  updateNodeById,
  removeNodeById,
  addChildToGroup,
  criteriaTreeBuilderValueToChecklistTree,
  criteriaChecklistTreeToCriteriaTreeBuilderValue,
} from "./criteria-tree-builder-utils";
export { DEFAULT_CRITERIA_TREE_BUILDER_LABELS } from "./criteria-tree-builder-context";

export interface CriteriaTreeBuilderProps {
  /** The criteria tree (controlled). */
  value: CriteriaTreeBuilderValue;
  /** Called with the updated tree on any edit. */
  onChange: (value: CriteriaTreeBuilderValue) => void;
  /** Renders the tree read-only: inputs are inert, add/remove actions are hidden. */
  disabled?: boolean;
  /** Allow the "+ Add Nested Group" action. @default true */
  allowNestedGroups?: boolean;
  /** Maximum nesting depth of groups below the root (root is depth 0). @default undefined (unlimited) */
  maxNestingDepth?: number;
  /** Override default labels (i18n). */
  labels?: Partial<typeof DEFAULT_CRITERIA_TREE_BUILDER_LABELS>;
  /** Extra class applied to the root element. */
  className?: string;
  /**
   * Custom statement input renderer. Renders the input for every condition row.
   */
  renderStatementInput: (
    args: CriteriaTreeBuilderRenderStatementInputArgs,
  ) => React.ReactNode;
}

/**
 * A visual, nested AND/OR builder for free-text statements.
 *
 * Renders a tree of statement rows grouped under AND/OR conjunctions, with
 * support for arbitrarily nested groups. Each row is a free-text input the
 * user writes into; the resulting tree is JSON-serializable.
 *
 * ```tsx
 * <CriteriaTreeBuilder value={tree} onChange={setTree} />
 * ```
 */
export const CriteriaTreeBuilder = ({
  value,
  onChange,
  disabled = false,
  allowNestedGroups = true,
  maxNestingDepth,
  labels: labelOverrides,
  className = "",
  renderStatementInput,
}: CriteriaTreeBuilderProps) => {
  const labels = useMemo(
    () => ({ ...DEFAULT_CRITERIA_TREE_BUILDER_LABELS, ...labelOverrides }),
    [labelOverrides],
  );

  const {
    updateStatement,
    toggleGroupConjunction,
    addCondition,
    addNestedGroup,
    removeNode,
  } = useCriteriaTreeBuilder(value, onChange);

  const ctx = useMemo(
    () => ({
      disabled,
      labels,
      allowNestedGroups,
      maxNestingDepth,
      renderStatementInput,
    }),
    [
      disabled,
      labels,
      allowNestedGroups,
      maxNestingDepth,
      renderStatementInput,
    ],
  );

  return (
    <CriteriaTreeBuilderCtx.Provider value={ctx}>
      <div className={`criteria-tree-builder ${className}`.trim()}>
        <CriteriaGroup
          group={value}
          isRoot
          onStatementChange={updateStatement}
          onToggleConjunction={toggleGroupConjunction}
          onAddCondition={addCondition}
          onAddNestedGroup={addNestedGroup}
          onRemove={removeNode}
        />
      </div>
    </CriteriaTreeBuilderCtx.Provider>
  );
};

export default CriteriaTreeBuilder;